import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton } from "../components/ui";
import {
  ApiRequestError,
  clearBlogVote,
  createBlogComment,
  fetchBlogComments,
  fetchBlogDetail,
  voteBlog,
} from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { resolvePublicAuthorName, toOptimisticAuthorNameParts } from "../lib/authorNames";
import { assetUrl } from "../lib/assets";
import { BlogRecipeSections } from "../components/BlogRecipeSections";
import { buildThreadedCommentTree, type ThreadedCommentNode } from "../lib/commentThread";
import { estimateBlogReadingTimeLabel, resolveBlogDisplayStyle, resolveBlogHeroMedia, resolveMediaAltText } from "../lib/blogPosts";
import { useShellSession } from "../hooks/useShellSession";
import { BlogRichContent } from "../components/BlogRichContent";
import type { BlogComment, BlogPost } from "../types";

const ROOT_COMMENT_PAGE_SIZE = 20;
const REPLY_COMMENT_PAGE_SIZE = 10;

function formatDate(value?: string | null): string {
  if (!value) {
    return "Unknown";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function withDepth(comment: BlogComment, fallbackDepth = 0): BlogComment {
  const depth = typeof comment.depth === "number" && Number.isFinite(comment.depth) ? Math.max(0, Math.trunc(comment.depth)) : fallbackDepth;
  return {
    ...comment,
    depth,
  };
}

function mergeUniqueComments(current: BlogComment[], incoming: BlogComment[]): BlogComment[] {
  const seen = new Set<string>();
  const merged: BlogComment[] = [];
  for (const comment of [...current, ...incoming]) {
    if (seen.has(comment.id)) {
      continue;
    }
    seen.add(comment.id);
    merged.push(comment);
  }
  return merged;
}

function buildOptimisticComment(args: {
  body: string;
  currentUserName: string | undefined;
  blogId: string;
  parentCommentId?: string | null;
  threadRootCommentId?: string | null;
  depth: number;
}): BlogComment {
  const optimisticAuthor = toOptimisticAuthorNameParts(args.currentUserName);
  const now = new Date().toISOString();
  return {
    id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    blog_id: args.blogId,
    body: args.body,
    created_at: now,
    updated_at: now,
    parent_comment_id: args.parentCommentId ?? null,
    thread_root_comment_id: args.threadRootCommentId ?? null,
    depth: Math.max(0, args.depth),
    author_display_name: optimisticAuthor.author_display_name ?? null,
    author_first_name: optimisticAuthor.author_first_name ?? null,
    author_last_initial: optimisticAuthor.author_last_initial ?? null,
    author_label: resolvePublicAuthorName(optimisticAuthor),
    author_user_id: null,
  };
}

function commentIndentStyle(comment: BlogComment): { marginLeft: string } {
  const depth = Math.max(0, Math.min(6, Math.trunc(comment.depth ?? 0)));
  return { marginLeft: `${depth * 14}px` };
}

function mergeCommentWithOptimisticAuthor(serverComment: BlogComment, optimisticComment: BlogComment): BlogComment {
  const serverLabel = resolvePublicAuthorName(serverComment);
  if (serverLabel !== "Anonymous") {
    return serverComment;
  }

  return {
    ...serverComment,
    author_display_name: serverComment.author_display_name ?? optimisticComment.author_display_name ?? null,
    author_first_name: serverComment.author_first_name ?? optimisticComment.author_first_name ?? null,
    author_last_initial: serverComment.author_last_initial ?? optimisticComment.author_last_initial ?? null,
    author_label: serverComment.author_label ?? optimisticComment.author_label ?? null,
    parent_comment_id: serverComment.parent_comment_id ?? optimisticComment.parent_comment_id ?? null,
    thread_root_comment_id: serverComment.thread_root_comment_id ?? optimisticComment.thread_root_comment_id ?? null,
    depth: serverComment.depth ?? optimisticComment.depth ?? 0,
  };
}

interface DietitianBlogDetailPageProps {
  basePath?: string;
  publicView?: boolean;
}

export function DietitianBlogDetailPage({
  basePath = "/app/dietitian-says",
  publicView = false,
}: DietitianBlogDetailPageProps) {
  const { blogId = "" } = useParams();
  const { session } = useShellSession();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [blog, setBlog] = useState<BlogPost | null>(null);

  const [rootComments, setRootComments] = useState<BlogComment[]>([]);
  const [rootOffset, setRootOffset] = useState(0);
  const [hasMoreRoots, setHasMoreRoots] = useState(false);
  const [loadingMoreRoots, setLoadingMoreRoots] = useState(false);

  const [repliesByRoot, setRepliesByRoot] = useState<Record<string, BlogComment[]>>({});
  const [replyOffsetsByRoot, setReplyOffsetsByRoot] = useState<Record<string, number>>({});
  const [hasMoreRepliesByRoot, setHasMoreRepliesByRoot] = useState<Record<string, boolean>>({});
  const [loadingRepliesByRoot, setLoadingRepliesByRoot] = useState<Record<string, boolean>>({});
  const [expandedThreads, setExpandedThreads] = useState<Record<string, boolean>>({});

  const [commentDraft, setCommentDraft] = useState("");
  const [replyDraftsByCommentId, setReplyDraftsByCommentId] = useState<Record<string, string>>({});
  const [activeReplyTarget, setActiveReplyTarget] = useState<{ rootId: string; parentCommentId: string } | null>(null);
  const [savingComment, setSavingComment] = useState(false);
  const [savingReplyParentId, setSavingReplyParentId] = useState<string | null>(null);
  const [updatingVote, setUpdatingVote] = useState(false);

  const isDietitian = Boolean(session.user?.isDietitian);
  const isAuthenticated = Boolean(session.isAuthenticated);
  const currentUserId = session.user?.id === undefined || session.user?.id === null ? null : String(session.user.id);
  const currentUserNameParts = toOptimisticAuthorNameParts(session.user?.name);
  const canInteract = isAuthenticated && Boolean(currentUserId);
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const signInBase = session.userLoginUrl || session.loginUrl || "/app/auth/sign-in/user/";
  const signInSeparator = signInBase.includes("?") ? "&" : "?";
  const signInUrl = `${signInBase}${signInSeparator}next=${encodeURIComponent(currentPath)}`;
  const feedPath = basePath;

  const renderedCommentCount = useMemo(() => {
    let replyCount = 0;
    for (const replies of Object.values(repliesByRoot)) {
      replyCount += replies.length;
    }
    return rootComments.length + replyCount;
  }, [rootComments, repliesByRoot]);

  useEffect(() => {
    if (!toastMessage) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setToastMessage(null);
    }, 3200);
    return () => window.clearTimeout(timeout);
  }, [toastMessage]);

  async function loadInitialRootComments(currentBlogId: string): Promise<void> {
    const payload = await fetchBlogComments(currentBlogId, {
      limit: ROOT_COMMENT_PAGE_SIZE,
      offset: 0,
      topLevelOnly: true,
    });
    const incoming = (payload.items ?? []).map((comment) =>
      withDepth(comment, comment.parent_comment_id ? 1 : 0),
    );

    const threaded = buildThreadedCommentTree(incoming);
    const nextRoots = threaded.map((node) => withDepth(node.comment, 0));
    const fallbackReplies: Record<string, BlogComment[]> = {};
    const fallbackOffsets: Record<string, number> = {};
    const fallbackHasMore: Record<string, boolean> = {};

    for (const node of threaded) {
      if (node.children.length === 0) {
        continue;
      }
      const rootId = node.comment.id;
      const replies = node.children.map((child) => withDepth(child.comment, Math.max(1, (node.comment.depth ?? 0) + 1)));
      fallbackReplies[rootId] = replies;
      fallbackOffsets[rootId] = replies.length;
      fallbackHasMore[rootId] = false;
    }

    setRootComments(nextRoots);
    setRootOffset(incoming.length);
    setHasMoreRoots(incoming.length >= ROOT_COMMENT_PAGE_SIZE);
    setRepliesByRoot(fallbackReplies);
    setReplyOffsetsByRoot(fallbackOffsets);
    setHasMoreRepliesByRoot(fallbackHasMore);
    setLoadingRepliesByRoot({});
    setExpandedThreads({});
    setReplyDraftsByCommentId({});
    setActiveReplyTarget(null);
  }

  async function load(): Promise<void> {
    if (!blogId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [blogPayload] = await Promise.all([fetchBlogDetail(blogId), loadInitialRootComments(blogId)]);
      setBlog(blogPayload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load this post.");
      setBlog(null);
      setRootComments([]);
      setRepliesByRoot({});
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [blogId]);

  async function loadMoreRootComments(): Promise<void> {
    if (!blogId || loadingMoreRoots || !hasMoreRoots) {
      return;
    }
    setLoadingMoreRoots(true);
    setError(null);
    try {
      const payload = await fetchBlogComments(blogId, {
        limit: ROOT_COMMENT_PAGE_SIZE,
        offset: rootOffset,
        topLevelOnly: true,
      });
      const incoming = (payload.items ?? []).map((comment) => withDepth(comment, comment.parent_comment_id ? 1 : 0));
      const threaded = buildThreadedCommentTree(incoming);
      const nextRoots = threaded.map((node) => withDepth(node.comment, 0));
      setRootComments((current) => mergeUniqueComments(current, nextRoots));
      setRootOffset((current) => current + incoming.length);
      setHasMoreRoots(incoming.length >= ROOT_COMMENT_PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load more comments.");
    } finally {
      setLoadingMoreRoots(false);
    }
  }

  async function loadRepliesForRoot(
    rootCommentId: string,
    append: boolean,
    options?: { quiet?: boolean },
  ): Promise<void> {
    if (!blogId) {
      return;
    }
    if (loadingRepliesByRoot[rootCommentId]) {
      return;
    }

    const offset = append
      ? replyOffsetsByRoot[rootCommentId] ?? repliesByRoot[rootCommentId]?.length ?? 0
      : 0;

    setLoadingRepliesByRoot((current) => ({ ...current, [rootCommentId]: true }));
    if (!options?.quiet) {
      setError(null);
    }

    try {
      const payload = await fetchBlogComments(blogId, {
        limit: REPLY_COMMENT_PAGE_SIZE,
        offset,
        parentCommentId: rootCommentId,
      });
      const incoming = (payload.items ?? [])
        .map((comment) => withDepth(comment, 1))
        .filter((comment) => (comment.parent_comment_id ?? null) === rootCommentId);

      setRepliesByRoot((current) => {
        const existing = append ? current[rootCommentId] ?? [] : [];
        return {
          ...current,
          [rootCommentId]: mergeUniqueComments(existing, incoming),
        };
      });
      setReplyOffsetsByRoot((current) => ({ ...current, [rootCommentId]: offset + incoming.length }));
      setHasMoreRepliesByRoot((current) => ({ ...current, [rootCommentId]: incoming.length >= REPLY_COMMENT_PAGE_SIZE }));
      setExpandedThreads((current) => ({ ...current, [rootCommentId]: true }));
    } catch (err) {
      if (!options?.quiet) {
        setError(err instanceof Error ? err.message : "Unable to load replies for this thread.");
      }
    } finally {
      setLoadingRepliesByRoot((current) => ({ ...current, [rootCommentId]: false }));
    }
  }

  function toggleThread(rootCommentId: string): void {
    const isExpanded = Boolean(expandedThreads[rootCommentId]);
    if (isExpanded) {
      setExpandedThreads((current) => ({ ...current, [rootCommentId]: false }));
      return;
    }

    setExpandedThreads((current) => ({ ...current, [rootCommentId]: true }));
    if (!(rootCommentId in repliesByRoot)) {
      void loadRepliesForRoot(rootCommentId, false);
    }
  }

  async function handleVote(value: 1 | -1): Promise<void> {
    if (!blog || updatingVote) {
      return;
    }
    if (!canInteract) {
      setToastMessage("Sign in to like or vote on posts.");
      return;
    }
    setUpdatingVote(true);
    setError(null);
    const isClearingVote = (blog.viewer_vote || 0) === value;
    try {
      if (isClearingVote) {
        await clearBlogVote(blog.id);
      } else {
        await voteBlog(blog.id, value);
      }
      trackEvent("blog_vote_updated", {
        action: isClearingVote ? "clear" : "set",
        vote_value: isClearingVote ? 0 : value,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update reaction.");
    } finally {
      setUpdatingVote(false);
    }
  }

  async function handleTopLevelCommentSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!blog || savingComment) {
      return;
    }
    if (!canInteract) {
      setToastMessage("Sign in to comment on this post.");
      return;
    }

    const body = commentDraft.trim();
    if (!body) {
      setError("Write a comment before posting.");
      return;
    }

    setSavingComment(true);
    setError(null);

    const optimisticComment = buildOptimisticComment({
      body,
      currentUserName: session.user?.name,
      blogId: blog.id,
      parentCommentId: null,
      threadRootCommentId: null,
      depth: 0,
    });

    setRootComments((current) => [optimisticComment, ...current]);
    setCommentDraft("");

    try {
      const serverComment = withDepth(await createBlogComment(blog.id, body), 0);
      const createdComment = mergeCommentWithOptimisticAuthor(serverComment, optimisticComment);
      setRootComments((current) => {
        const withoutOptimistic = current.filter((entry) => entry.id !== optimisticComment.id);
        if (withoutOptimistic.some((entry) => entry.id === createdComment.id)) {
          return withoutOptimistic;
        }
        return [createdComment, ...withoutOptimistic];
      });
      trackEvent("blog_comment_created", { comment_length: body.length, is_reply: false });
    } catch (err) {
      setRootComments((current) => current.filter((entry) => entry.id !== optimisticComment.id));
      setCommentDraft(body);
      setError(err instanceof Error ? err.message : "Unable to post comment.");
    } finally {
      setSavingComment(false);
    }
  }

  function resolveCommentAuthorName(comment: BlogComment): string {
    const label = resolvePublicAuthorName(comment);
    if (label !== "Anonymous") {
      return label;
    }
    if (currentUserId && comment.author_user_id && String(comment.author_user_id) === currentUserId) {
      return resolvePublicAuthorName({
        author_display_name: comment.author_display_name ?? null,
        author_first_name: comment.author_first_name ?? currentUserNameParts.author_first_name ?? null,
        author_last_initial: comment.author_last_initial ?? currentUserNameParts.author_last_initial ?? null,
        legacy_label: comment.author_label ?? null,
      });
    }
    return label;
  }

  async function handleReplySubmit(
    rootCommentId: string,
    parentComment: BlogComment,
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (!blog || savingReplyParentId) {
      return;
    }
    if (!canInteract) {
      setToastMessage("Sign in to reply to comments.");
      return;
    }

    const body = (replyDraftsByCommentId[parentComment.id] || "").trim();
    if (!body) {
      setError("Write a reply before posting.");
      return;
    }

    const fallbackDepth = Math.max(1, (parentComment.depth ?? 0) + 1);
    const optimisticReply = buildOptimisticComment({
      body,
      currentUserName: session.user?.name,
      blogId: blog.id,
      parentCommentId: parentComment.id,
      threadRootCommentId: rootCommentId,
      depth: fallbackDepth,
    });

    setSavingReplyParentId(parentComment.id);
    setError(null);
    setRepliesByRoot((current) => ({
      ...current,
      [rootCommentId]: [optimisticReply, ...(current[rootCommentId] ?? [])],
    }));
    setExpandedThreads((current) => ({ ...current, [rootCommentId]: true }));
    setReplyDraftsByCommentId((current) => ({ ...current, [parentComment.id]: "" }));

    try {
      const serverReply = withDepth(await createBlogComment(blog.id, body, parentComment.id), fallbackDepth);
      const createdReply = mergeCommentWithOptimisticAuthor(serverReply, optimisticReply);
      setRepliesByRoot((current) => ({
        ...current,
        [rootCommentId]: (current[rootCommentId] ?? []).map((entry) =>
          entry.id === optimisticReply.id ? createdReply : entry,
        ),
      }));
      trackEvent("blog_comment_created", { comment_length: body.length, is_reply: true });
    } catch (err) {
      setRepliesByRoot((current) => ({
        ...current,
        [rootCommentId]: (current[rootCommentId] ?? []).filter((entry) => entry.id !== optimisticReply.id),
      }));
      setReplyDraftsByCommentId((current) => ({ ...current, [parentComment.id]: body }));

      if (err instanceof ApiRequestError && err.status === 404) {
        setToastMessage("That comment thread is no longer available. Refreshing replies.");
        await loadRepliesForRoot(rootCommentId, false, { quiet: true });
      } else {
        setError(err instanceof Error ? err.message : "Unable to post reply.");
      }
    } finally {
      setSavingReplyParentId(null);
    }
  }

  function renderThreadNode(rootCommentId: string, node: ThreadedCommentNode): JSX.Element {
    const comment = withDepth(node.comment, 0);
    const isReplyFormVisible =
      activeReplyTarget?.rootId === rootCommentId && activeReplyTarget.parentCommentId === comment.id;
    const isReplySaving = savingReplyParentId === comment.id;

    return (
      <div key={comment.id} className="space-y-2">
        <article
          className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-3"
          style={commentIndentStyle(comment)}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">{resolveCommentAuthorName(comment)}</p>
            <p className="text-xs text-[var(--pr-text-muted)]">{formatDate(comment.created_at)}</p>
          </div>
          <p className="mt-2 text-sm leading-relaxed">{comment.body}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {canInteract ? (
              <Button
                variant="secondary"
                className="min-h-9 px-3 py-2 text-xs"
                onClick={() =>
                  setActiveReplyTarget((current) =>
                    current?.rootId === rootCommentId && current.parentCommentId === comment.id
                      ? null
                      : { rootId: rootCommentId, parentCommentId: comment.id },
                  )
                }
              >
                {isReplyFormVisible ? "Cancel reply" : "Reply"}
              </Button>
            ) : null}
          </div>
        </article>

        {isReplyFormVisible && canInteract ? (
          <form
            className="space-y-2 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-3"
            style={{ marginLeft: "14px" }}
            onSubmit={(event) => void handleReplySubmit(rootCommentId, comment, event)}
          >
            <textarea
              value={replyDraftsByCommentId[comment.id] ?? ""}
              onChange={(event) =>
                setReplyDraftsByCommentId((current) => ({ ...current, [comment.id]: event.target.value }))
              }
              rows={2}
              placeholder="Write a reply..."
              className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-sm"
            />
            <div className="flex justify-end">
              <Button type="submit" className="min-h-10 px-3 text-sm" disabled={isReplySaving}>
                {isReplySaving ? "Posting reply..." : "Post reply"}
              </Button>
            </div>
          </form>
        ) : null}

        {node.children.length > 0 ? (
          <div className="space-y-2">{node.children.map((child) => renderThreadNode(rootCommentId, child))}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div data-testid="dietitian-blog-detail-page" className="space-y-4 pb-8">
      {toastMessage ? (
        <div className="pr-toast sticky top-3 z-20 px-4 py-2 text-sm text-[var(--pr-text-primary)]">{toastMessage}</div>
      ) : null}

      <PageHeader
        title="Dietitian post"
        subtitle={blogId}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <Link to={feedPath} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
              Back to feed
            </Link>
            {isDietitian && blog && !publicView ? (
              <Link to={`${basePath}/${blog.id}/edit`} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
                Edit post
              </Link>
            ) : null}
          </div>
        }
      />

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-64" />
          <Skeleton className="h-28" />
          <Skeleton className="h-40" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : !blog ? (
        <ErrorState message="Post not found." onRetry={() => void load()} />
      ) : (
        <>
          {(() => {
            const displayStyle = resolveBlogDisplayStyle(blog);
            const isRecipe = displayStyle === "RECIPE";
            const heroMedia = resolveBlogHeroMedia(blog);
            const heroImage = heroMedia?.url || assetUrl("pantry-inventory-bg.png");
            const heroAlt = resolveMediaAltText(heroMedia, blog.title);
            const servingText = blog.structuredContent?.servingText;

            return (
          <Card className="overflow-hidden p-0">
            <div className="relative h-64 sm:h-72">
              <img src={heroImage} alt={heroAlt} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-black/10" />
              <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
                <div className="flex flex-wrap gap-2">
                  {isRecipe ? <Pill tone="accent" label="RECIPE" /> : null}
                  {(blog.tags || []).map((tag) => (
                    <Pill key={`${blog.id}-${tag}`} label={tag} />
                  ))}
                  {(blog.status || "").toUpperCase() === "DRAFT" ? <Pill tone="accent" label="DRAFT" /> : null}
                </div>
                <h2 className="pr-display mt-2 text-2xl font-semibold sm:text-3xl">{blog.title}</h2>
                <p className="mt-1 line-clamp-2 text-sm text-white/90">
                  {blog.summary || "Pantry guidance from PantryRx dietitians."}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/85">
                  <span>{resolvePublicAuthorName(blog)}</span>
                  <span>{formatDate(blog.published_at || blog.created_at)}</span>
                  <span>{isRecipe && servingText ? servingText : estimateBlogReadingTimeLabel(blog)}</span>
                  <span>{blog.comment_count ?? renderedCommentCount} comments</span>
                </div>
              </div>
            </div>
          </Card>
            );
          })()}

          <Card>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={blog.viewer_vote === 1 ? "primary" : "secondary"}
                className="min-h-10 px-3 text-sm"
                disabled={updatingVote || !canInteract}
                onClick={() => void handleVote(1)}
              >
                Helpful ({blog.upvotes ?? 0})
              </Button>
              <Button
                variant={blog.viewer_vote === -1 ? "primary" : "secondary"}
                className="min-h-10 px-3 text-sm"
                disabled={updatingVote || !canInteract}
                onClick={() => void handleVote(-1)}
              >
                Needs work ({blog.downvotes ?? 0})
              </Button>
              <Pill tone="accent" label={`Score ${blog.score ?? 0}`} />
            </div>
            {!canInteract ? (
              <p className="mt-2 text-sm text-[var(--pr-text-muted)]">
                <a href={signInUrl} className="font-semibold text-[var(--pr-accent-deep)] underline underline-offset-4">
                  Sign in
                </a>{" "}
                to vote and react to this post.
              </p>
            ) : null}
          </Card>

          {resolveBlogDisplayStyle(blog) === "RECIPE" && blog.structuredContent ? (
            <Card className="px-4 py-4 sm:px-5 sm:py-5">
              <BlogRecipeSections
                structuredContent={blog.structuredContent}
                emptyMessage="Recipe details will appear here as soon as the dietitian adds them."
              />
            </Card>
          ) : (
            <Card className="px-4 py-4 sm:px-5 sm:py-5">
              <BlogRichContent body={blog.body} className="mx-auto w-full max-w-[72ch]" />
            </Card>
          )}

          <Card>
            <h3 className="text-base font-semibold">Comments</h3>
            <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Share practical feedback and follow-up questions.</p>

            {canInteract ? (
              <form className="mt-3 space-y-2" onSubmit={(event) => void handleTopLevelCommentSubmit(event)}>
                <textarea
                  value={commentDraft}
                  onChange={(event) => setCommentDraft(event.target.value)}
                  rows={3}
                  placeholder="Add a thoughtful comment..."
                  className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-sm"
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={savingComment}>
                    {savingComment ? "Posting..." : "Post comment"}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="mt-3 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3 text-sm text-[var(--pr-text-muted)]">
                <a href={signInUrl} className="font-semibold text-[var(--pr-accent-deep)] underline underline-offset-4">
                  Sign in
                </a>{" "}
                to post comments and replies.
              </div>
            )}

            {rootComments.length === 0 ? (
              <EmptyState title="No comments yet" subtitle="Be the first to respond to this dietitian post." />
            ) : (
              <div className="mt-3 space-y-3">
                {rootComments.map((rootComment) => {
                  const rootId = rootComment.id;
                  const replies = repliesByRoot[rootId] ?? [];
                  const threadNodes = buildThreadedCommentTree([withDepth(rootComment, 0), ...replies]);
                  const rootNode =
                    threadNodes.find((entry) => entry.comment.id === rootId) ?? {
                      comment: withDepth(rootComment, 0),
                      children: [],
                    };
                  const isExpanded = Boolean(expandedThreads[rootId]);
                  const isLoadingReplies = Boolean(loadingRepliesByRoot[rootId]);
                  const hasLoadedReplies = rootId in repliesByRoot;
                  const hasMoreReplies = Boolean(hasMoreRepliesByRoot[rootId]);
                  const isReplyFormVisible =
                    activeReplyTarget?.rootId === rootId && activeReplyTarget.parentCommentId === rootId;
                  const isReplySaving = savingReplyParentId === rootId;

                  return (
                    <div key={rootId} className="space-y-2">
                      <article
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3"
                        style={commentIndentStyle(rootNode.comment)}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold">{resolveCommentAuthorName(rootNode.comment)}</p>
                          <p className="text-xs text-[var(--pr-text-muted)]">{formatDate(rootNode.comment.created_at)}</p>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed">{rootNode.comment.body}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {canInteract ? (
                            <Button
                              variant="secondary"
                              className="min-h-9 px-3 py-2 text-xs"
                              onClick={() =>
                                setActiveReplyTarget((current) =>
                                  current?.rootId === rootId && current.parentCommentId === rootId
                                    ? null
                                    : { rootId, parentCommentId: rootId },
                                )
                              }
                            >
                              {isReplyFormVisible ? "Cancel reply" : "Reply"}
                            </Button>
                          ) : null}
                          <Button
                            variant="secondary"
                            className="min-h-9 px-3 py-2 text-xs"
                            onClick={() => toggleThread(rootId)}
                          >
                            {isExpanded ? "Hide replies" : hasLoadedReplies ? `Show replies (${replies.length})` : "Load replies"}
                          </Button>
                        </div>
                      </article>

                      {isReplyFormVisible && canInteract ? (
                        <form
                          className="space-y-2 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-3"
                          style={{ marginLeft: "14px" }}
                          onSubmit={(event) => void handleReplySubmit(rootId, rootNode.comment, event)}
                        >
                          <textarea
                            value={replyDraftsByCommentId[rootId] ?? ""}
                            onChange={(event) =>
                              setReplyDraftsByCommentId((current) => ({ ...current, [rootId]: event.target.value }))
                            }
                            rows={2}
                            placeholder="Write a reply..."
                            className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-sm"
                          />
                          <div className="flex justify-end">
                            <Button type="submit" className="min-h-10 px-3 text-sm" disabled={isReplySaving}>
                              {isReplySaving ? "Posting reply..." : "Post reply"}
                            </Button>
                          </div>
                        </form>
                      ) : null}

                      {isExpanded ? (
                        <div className="space-y-2">
                          {isLoadingReplies && replies.length === 0 ? (
                            <p className="text-xs text-[var(--pr-text-muted)]" style={{ marginLeft: "14px" }}>
                              Loading replies...
                            </p>
                          ) : null}

                          {rootNode.children.map((child) => renderThreadNode(rootId, child))}

                          {!isLoadingReplies && rootNode.children.length === 0 ? (
                            <p className="text-xs text-[var(--pr-text-muted)]" style={{ marginLeft: "14px" }}>
                              No replies yet.
                            </p>
                          ) : null}

                          {hasMoreReplies ? (
                            <div style={{ marginLeft: "14px" }}>
                              <Button
                                variant="secondary"
                                className="min-h-9 px-3 py-2 text-xs"
                                disabled={isLoadingReplies}
                                onClick={() => void loadRepliesForRoot(rootId, true)}
                              >
                                {isLoadingReplies ? "Loading..." : "Load more replies"}
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {hasMoreRoots ? (
                  <div className="pt-1">
                    <Button variant="secondary" className="min-h-10 px-3 text-sm" disabled={loadingMoreRoots} onClick={() => void loadMoreRootComments()}>
                      {loadingMoreRoots ? "Loading..." : "Load more comments"}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
